import classNames from 'classnames'
import { useState } from 'react'
import { formatDecimalPlaces } from '../../../../helpers/functions/format-decimal-places'
import {
  useTranslate,
  type Language,
} from '../../../../hooks/language/use-translate'
import { useGetUserTaskHostFirst } from '../../../../hooks/queries/users-tasks/users-tasks-hosts/use-get-user-task-host'
import { useRescuedHostTask } from '../../../../hooks/queries/users-tasks/users-tasks-hosts/users-tasks-hosts-rescued/use-rescued-host-task'
import { useGetUserTaskSpectatorFirst } from '../../../../hooks/queries/users-tasks/users-tasks-spectators/use-get-user-task-spectator'
import { useRescuedSpectatorTask } from '../../../../hooks/queries/users-tasks/users-tasks-spectators/users-tasks-spectators-rescued/use-rescued-spectator-task'
import type { UserTaskHostDefault } from '../../../../services/users-tasks/defaults/users-tasks-hosts-defaults/types'
import type { UserTaskSpectatorDefault } from '../../../../services/users-tasks/defaults/users-tasks-spectators-defaults/types'
import { ModalRescueTask } from '../../modals/modal-rescue-task'
import { Reward } from '../reward'

interface Props {
  task: UserTaskSpectatorDefault | UserTaskHostDefault
  type: 'spectator' | 'host'
}

export const TaskItem = ({ task, type }: Props) => {
  const [isModalRescueTaskVisible, setIsModalRescueTaskVisible] =
    useState(false)

  const isSpectator = type === 'spectator'

  const { userTaskSpectator } = useGetUserTaskSpectatorFirst(
    task?.id,
    isSpectator,
  )

  const { userTaskHost } = useGetUserTaskHostFirst(task?.id, !isSpectator)

  const { rescue: rescueHost } = useRescuedHostTask()
  const { rescue: rescueSpectator } = useRescuedSpectatorTask()

  const { translate } = useTranslate()

  const currentTask = isSpectator ? userTaskSpectator : userTaskHost
  const name = task?.name?.replaceAll('_', '-')

  const quantityMade = formatDecimalPlaces(currentTask?.quantityCompleted ?? 0)
  const totalQuantity = formatDecimalPlaces(task?.quantity)

  const disabled =
    currentTask?.rescued ||
    (currentTask?.quantityCompleted || 0) < task?.quantity

  async function handleRescueTask() {
    if (isSpectator) {
      const response = await rescueSpectator({ userTaskSpectatorId: task.id })

      if (response) {
        setIsModalRescueTaskVisible(true)
      }
    }

    if (!isSpectator) {
      const response = await rescueHost({ userTaskHostId: task.id })

      if (response) {
        setIsModalRescueTaskVisible(true)
      }
    }
  }

  return (
    <div className="flex items-center w-full justify-between p-4 bg-white  border-b-2 border-b-gray-50 mb-2">
      <ModalRescueTask
        visible={isModalRescueTaskVisible}
        task={task}
        handleCloseModal={() => setIsModalRescueTaskVisible(false)}
      />

      <div className="flex items-start gap-3">
        <div className="p-2 rounded-xl bg-purple-100 flex items-center justify-center">
          {task?.userTaskBonusType === 'appCoin' && <Reward coins />}

          {task?.userTaskBonusType === 'appPoint' && <Reward points />}

          {task?.userTaskBonusType === 'appBean' && <Reward beans />}

          {!['appCoin', 'appPoint', 'appBean'].includes(
            task?.userTaskBonusType,
          ) && <Reward gift />}
        </div>

        <div>
          <h3 className="text-gray-900 font-semibold text-sm leading-tight">
            {translate(name as Language)}
          </h3>

          <p className="text-gray-400 text-xs mt-1">
            ({quantityMade}/{totalQuantity})
          </p>
        </div>
      </div>

      <button
        disabled={disabled}
        onClick={handleRescueTask}
        className={classNames(
          'w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center',
          {
            'bg-yellow-400': !disabled,
            'bg-gray-100': disabled,
          },
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={classNames('w-5 h-5 ', {
            'text-gray-400': disabled,
            'text-white': !disabled,
          })}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      </button>
    </div>
  )
}
